import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────
interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in red. Use for destructive actions. */
  destructive?: boolean;
  /**
   * Info-only: hides Cancel and renders a single acknowledge button. This is the
   * in-app replacement for `window.alert` — same dialog chrome as a confirm, so
   * errors and validation messages stop dropping out to an unbranded, blocking
   * browser popup. Prefer `useAlert()` over passing this by hand.
   */
  alert?: boolean;
  /**
   * Renders a text field in the dialog — the `window.prompt` replacement.
   * Prefer `usePrompt()`, which resolves to the entered string (or null when
   * the user cancels) instead of a bare boolean.
   */
  input?: {
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    /** Renders a textarea instead of a single-line input. */
    multiline?: boolean;
    /** Disables the confirm button until the field is non-empty. */
    required?: boolean;
  };
}

/** What the dialog resolves with internally: outcome + the input's value. */
interface DialogResult {
  ok: boolean;
  value: string;
}

type DialogFn = (opts: ConfirmOptions) => Promise<DialogResult>;

const ConfirmContext = createContext<DialogFn | null>(null);

function useDialog(): DialogFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm/useAlert/usePrompt must be used inside <ConfirmProvider>');
  return fn;
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const dialog = useDialog();
  return useCallback((opts) => dialog(opts).then((r) => r.ok), [dialog]);
}

/**
 * `window.alert` replacement — resolves once the user acknowledges.
 *
 *   const alertDialog = useAlert();
 *   await alertDialog({ title: 'Could not save', description: extractApiError(e) });
 */
export function useAlert(): (opts: Omit<ConfirmOptions, 'alert' | 'input'>) => Promise<boolean> {
  const dialog = useDialog();
  return useCallback((opts) => dialog({ ...opts, alert: true }).then((r) => r.ok), [dialog]);
}

/**
 * `window.prompt` replacement — resolves with the entered text, or null if the
 * user cancelled. An empty string is a real answer (the caller decides whether
 * that is allowed via `input.required`), so callers must check for null rather
 * than falsiness when the distinction matters.
 *
 *   const promptDialog = usePrompt();
 *   const notes = await promptDialog({ title: 'Resolve', input: { multiline: true } });
 *   if (notes === null) return;            // cancelled
 */
export function usePrompt(): (opts: ConfirmOptions) => Promise<string | null> {
  const dialog = useDialog();
  return useCallback(
    (opts) => dialog({ input: {}, ...opts }).then((r) => (r.ok ? r.value : null)),
    [dialog]
  );
}

// ─── Provider ───────────────────────────────────────────────────────────────
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((r: DialogResult) => void) | null>(null);

  const dialog: DialogFn = useCallback(
    (opts) =>
      new Promise<DialogResult>((resolve) => {
        resolveRef.current = resolve;
        setOpen(opts);
      }),
    []
  );

  const close = (ok: boolean, value = '') => {
    if (resolveRef.current) resolveRef.current({ ok, value });
    resolveRef.current = null;
    setOpen(null);
  };

  return (
    <ConfirmContext.Provider value={dialog}>
      {children}
      {open && (
        <ConfirmDialog
          // Remount per invocation so the input starts from `defaultValue`
          // instead of inheriting the previous dialog's text.
          key={open.title}
          options={open}
          onCancel={() => close(false)}
          onConfirm={(value) => close(true, value)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  options,
  onCancel,
  onConfirm,
}: {
  options: ConfirmOptions;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}) {
  const [value, setValue] = useState(options.input?.defaultValue ?? '');
  const blocked = Boolean(options.input?.required) && !value.trim();
  const submit = () => { if (!blocked) onConfirm(value); };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'var(--text-strong)',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-md)',
    outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          width: 'min(92vw, 440px)',
          padding: 24,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)', marginBottom: options.description ? 8 : 16 }}>
          {options.title}
        </div>
        {options.description && (
          // pre-wrap so multi-line messages (e.g. a list of skipped rows from a
          // bulk import) keep their line breaks instead of collapsing to one run.
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {options.description}
          </div>
        )}
        {options.input && (
          <div style={{ marginBottom: 20 }}>
            {options.input.label && (
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                {options.input.label}
              </label>
            )}
            {options.input.multiline ? (
              <textarea
                autoFocus
                rows={3}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={options.input.placeholder}
                style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            ) : (
              <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder={options.input.placeholder}
                style={fieldStyle}
              />
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!options.alert && (
            <button
              onClick={onCancel}
              style={{
                background: 'var(--surface-subtle)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {options.cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            onClick={submit}
            disabled={blocked}
            autoFocus={!options.input}
            style={{
              background: options.destructive ? 'var(--danger)' : 'var(--pr, var(--info))',
              color: 'var(--brand-contrast)',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: blocked ? 'not-allowed' : 'pointer',
              opacity: blocked ? 0.5 : 1,
            }}
          >
            {options.confirmLabel ?? (options.alert ? 'OK' : options.destructive ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
